import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { type CanActivateFn, Router, type UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthApiService } from './auth-api.service';
import { AuthService } from './auth.service';
import { PermissionService } from '../authorization/permission.service';
import { EnterpriseAccessApiService } from '../platform/enterprise-access-api.service';
import {
  CONTEXT_RETURN_URL_QUERY_PARAM,
  LOGIN_REASON_QUERY_PARAM,
  normalizeContextReturnUrl,
  SESSION_EXPIRED_LOGIN_REASON,
} from '../platform/context-return-url';
import { PlatformContextService } from '../platform/platform-context.service';

export const authGuard: CanActivateFn = async (_route, state) => {
  const authApi = inject(AuthApiService);
  const authService = inject(AuthService);
  const enterpriseAccessApi = inject(EnterpriseAccessApiService);
  const permissionService = inject(PermissionService);
  const platformContext = inject(PlatformContextService);
  const router = inject(Router);

  if (!(await authService.restoreSession())) {
    platformContext.clear();
    return loginRedirect(router, state.url);
  }

  platformContext.rememberApplicationRequest(state.url);

  if (platformContext.applicationState() !== 'ready') {
    platformContext.beginApplicationPreparation();
  }

  try {
    const currentUser = await firstValueFrom(authApi.getCurrentUser());
    if (!currentUser.isAuthenticated || !currentUser.cognitoSub) {
      await authService.invalidateSession();
      return loginRedirect(router, state.url, SESSION_EXPIRED_LOGIN_REASON);
    }

    platformContext.initialize(currentUser);
  } catch (error) {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      await authService.invalidateSession();
      return loginRedirect(router, state.url, SESSION_EXPIRED_LOGIN_REASON);
    }

    platformContext.markUnavailable();
    platformContext.markApplicationError();
    return isTenantSelectionUrl(state.url)
      ? true
      : contextSelectionRedirect(router, '/select-tenant', state.url);
  }

  const tenantNavigation = resolveTenantNavigation(router, state.url, platformContext);
  if (tenantNavigation !== null) {
    return tenantNavigation;
  }

  if (platformContext.needsEnterpriseAccessLoad()) {
    platformContext.beginEnterpriseAccessLoad();
    try {
      platformContext.initializeEnterpriseAccess(
        await firstValueFrom(enterpriseAccessApi.getEnterpriseAccess()),
      );
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 403)) {
        platformContext.markEnterpriseContextUnavailable();
      } else {
        platformContext.markEnterpriseUnavailable();
      }
    }
  }

  const enterpriseNavigation = resolveEnterpriseNavigation(router, state.url, platformContext);
  const contextComplete =
    platformContext.enterpriseState() === 'ready' &&
    platformContext.selectedCompany() !== null &&
    platformContext.selectedBranch() !== null;

  if (!contextComplete) {
    return enterpriseNavigation;
  }

  // /context/permissions is resolved with the selected tenant, company and branch
  // headers, so a successful response also proves the backend BusinessContext.
  if (!(await permissionService.load())) {
    platformContext.markApplicationError();
    return false;
  }

  platformContext.markApplicationReady();
  return enterpriseNavigation;
};

export const guestGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  return (await authService.restoreSession()) ? router.createUrlTree(['/']) : true;
};

function resolveTenantNavigation(
  router: Router,
  currentUrl: string,
  context: PlatformContextService,
): true | UrlTree | null {
  const selectionRoute = isTenantSelectionUrl(currentUrl);
  const selectionRequired =
    context.platformUserStatus() !== 'active' ||
    context.availableTenants().length === 0 ||
    context.selectedTenant() === null;

  if (selectionRequired) {
    context.requireApplicationIntervention();
    return selectionRoute ? true : contextSelectionRedirect(router, '/select-tenant', currentUrl);
  }

  if (selectionRoute) {
    context.requireApplicationIntervention();
    return true;
  }

  return null;
}

function resolveEnterpriseNavigation(
  router: Router,
  currentUrl: string,
  context: PlatformContextService,
): true | UrlTree {
  const selectionRoute = isEnterpriseSelectionUrl(currentUrl);
  const complete =
    context.enterpriseState() === 'ready' &&
    context.selectedCompany() !== null &&
    context.selectedBranch() !== null;

  if (complete) {
    return selectionRoute ? router.createUrlTree(['/']) : true;
  }

  if (
    context.enterpriseState() === 'contextUnavailable' ||
    context.enterpriseState() === 'unavailable'
  ) {
    context.markApplicationError();
  } else {
    context.requireApplicationIntervention();
  }

  return selectionRoute ? true : contextSelectionRedirect(router, '/select-context', currentUrl);
}

function isTenantSelectionUrl(url: string): boolean {
  return url.split('?')[0] === '/select-tenant';
}

function isEnterpriseSelectionUrl(url: string): boolean {
  return url.split('?')[0] === '/select-context';
}

function contextSelectionRedirect(
  router: Router,
  selectionPath: '/select-tenant' | '/select-context',
  returnUrl: string,
): UrlTree {
  return router.createUrlTree([selectionPath], {
    queryParams: {
      [CONTEXT_RETURN_URL_QUERY_PARAM]: normalizeContextReturnUrl(returnUrl),
    },
  });
}

function loginRedirect(
  router: Router,
  returnUrl: string,
  reason?: typeof SESSION_EXPIRED_LOGIN_REASON,
): UrlTree {
  return router.createUrlTree(['/login'], {
    queryParams: {
      [CONTEXT_RETURN_URL_QUERY_PARAM]: returnUrl,
      ...(reason === undefined ? {} : { [LOGIN_REASON_QUERY_PARAM]: reason }),
    },
  });
}
